import { dasherize } from '@zoocasa/node-kit/strings/dasherize';
import { dasherizeKeys } from '@zoocasa/node-kit/objects/transform-keys';
import { swapKeysAndValues } from '@zoocasa/node-kit/objects/swap-keys-and-values';
import { CountryCode, CountryCodeList } from 'types/countries';


export type CanadaProvinceCode = 'on' | 'pe' | 'qc' | 'nl' | 'ns' | 'bc' | 'mb' | 'ab' | 'sk' | 'nt' | 'nb' | 'nu' | 'yt';
export type CanadaProvince = 'Ontario' | 'Prince Edward Island' | 'Quebec' | 'Newfoundland and Labrador' | 'Nova Scotia' | 'British Columbia' | 'Manitoba' | 'Alberta' | 'Saskatchewan' | 'Northwest Territories' | 'New Brunswick' | 'Nunavut' | 'Yukon';

export type UsaStateCode = 'al' | 'ak' | 'as' | 'az' | 'ar' | 'ca' | 'co' | 'ct' | 'de' | 'dc' | 'fm' | 'fl' | 'ga' | 'gu' | 'hi' | 'id' | 'il' | 'in' | 'ia' | 'ks' | 'ky' | 'la' | 'me' | 'mh' | 'md' | 'ma' | 'mi' | 'mn' | 'ms' | 'mo' | 'mt' | 'ne' | 'nv' | 'nh' | 'nj' | 'nm' | 'ny' | 'nc' | 'nd' | 'mp' | 'oh' | 'ok' | 'or' | 'pw' | 'pa' | 'pr' | 'ri' | 'sc' | 'sd' | 'tn' | 'tx' | 'ut' | 'vt' | 'vi' | 'va' | 'wa' | 'wv' | 'wi' | 'wy';
export type UsaState = 'Alabama' | 'Alaska' | 'American Samoa' | 'Arizona' | 'Arkansas' | 'California' | 'Colorado' | 'Connecticut' | 'Delaware' | 'District Of Columbia' | 'Federated States Of Micronesia' | 'Florida' | 'Georgia' | 'Guam' | 'Hawaii' | 'Idaho' | 'Illinois' | 'Indiana' | 'Iowa' | 'Kansas' | 'Kentucky' | 'Louisiana' | 'Maine' | 'Marshall Islands' | 'Maryland' | 'Massachusetts' | 'Michigan' | 'Minnesota' | 'Mississippi' | 'Missouri' | 'Montana' | 'Nebraska' | 'Nevada' | 'New Hampshire' | 'New Jersey' | 'New Mexico' | 'New York' | 'North Carolina' | 'North Dakota' | 'Northern Mariana Islands' | 'Ohio' | 'Oklahoma' | 'Oregon' | 'Palau' | 'Pennsylvania' | 'Puerto Rico' | 'Rhode Island' | 'South Carolina' | 'South Dakota' | 'Tennessee' | 'Texas' | 'Utah' | 'Vermont' | 'Virgin Islands' | 'Virginia' | 'Washington' | 'West Virginia' | 'Wisconsin' | 'Wyoming';
export type ProvinceOrStateCode = CanadaProvinceCode | UsaStateCode;
export type ProvinceOrState = CanadaProvince | UsaState;
export enum ProvinceAndState {
  AB = 'AB',
  BC = 'BC',
  MB = 'MB',
  NB = 'NB',
  NL = 'NL',
  NS = 'NS',
  NT = 'NT',
  NU = 'NU',
  ON = 'ON',
  PE = 'PE',
  QC = 'QC',
  SK = 'SK',
  YT = 'YT',
  AL = 'AL',
  AK = 'AK',
  AS = 'AS',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DE = 'DE',
  DC = 'DC',
  FM = 'FM',
  FL = 'FL',
  GA = 'GA',
  GU = 'GU',
  HI = 'HI',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  IA = 'IA',
  KS = 'KS',
  KY = 'KY',
  LA = 'LA',
  ME = 'ME',
  MH = 'MH',
  MD = 'MD',
  MA = 'MA',
  MI = 'MI',
  MN = 'MN',
  MS = 'MS',
  MO = 'MO',
  MT = 'MT',
  NE = 'NE',
  NV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  MP = 'MP',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PW = 'PW',
  PA = 'PA',
  PR = 'PR',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VT = 'VT',
  VI = 'VI',
  VA = 'VA',
  WA = 'WA',
  WV = 'WV',
  WI = 'WI',
  WY = 'WY'
}


const provinceMappings: Record<CanadaProvinceCode, CanadaProvince> = {
  on: 'Ontario',
  pe: 'Prince Edward Island',
  qc: 'Quebec',
  nl: 'Newfoundland and Labrador',
  ns: 'Nova Scotia',
  bc: 'British Columbia',
  mb: 'Manitoba',
  ab: 'Alberta',
  sk: 'Saskatchewan',
  nt: 'Northwest Territories',
  nb: 'New Brunswick',
  nu: 'Nunavut',
  yt: 'Yukon',
} as const;

const provinceNameMappings: Record<CanadaProvince, CanadaProvinceCode> = {
  Ontario: 'on',
  'Prince Edward Island': 'pe',
  Quebec: 'qc',
  'Newfoundland and Labrador': 'nl',
  'Nova Scotia': 'ns',
  'British Columbia': 'bc',
  Manitoba: 'mb',
  Alberta: 'ab',
  Saskatchewan: 'sk',
  'Northwest Territories': 'nt',
  'New Brunswick': 'nb',
  Nunavut: 'nu',
  Yukon: 'yt',
} as const;

const stateMappings: Record<UsaStateCode, UsaState> = {
  al: 'Alabama',
  ak: 'Alaska',
  as: 'American Samoa',
  az: 'Arizona',
  ar: 'Arkansas',
  ca: 'California',
  co: 'Colorado',
  ct: 'Connecticut',
  de: 'Delaware',
  dc: 'District Of Columbia',
  fm: 'Federated States Of Micronesia',
  fl: 'Florida',
  ga: 'Georgia',
  gu: 'Guam',
  hi: 'Hawaii',
  id: 'Idaho',
  il: 'Illinois',
  in: 'Indiana',
  ia: 'Iowa',
  ks: 'Kansas',
  ky: 'Kentucky',
  la: 'Louisiana',
  me: 'Maine',
  mh: 'Marshall Islands',
  md: 'Maryland',
  ma: 'Massachusetts',
  mi: 'Michigan',
  mn: 'Minnesota',
  ms: 'Mississippi',
  mo: 'Missouri',
  mt: 'Montana',
  ne: 'Nebraska',
  nv: 'Nevada',
  nh: 'New Hampshire',
  nj: 'New Jersey',
  nm: 'New Mexico',
  ny: 'New York',
  nc: 'North Carolina',
  nd: 'North Dakota',
  mp: 'Northern Mariana Islands',
  oh: 'Ohio',
  ok: 'Oklahoma',
  or: 'Oregon',
  pw: 'Palau',
  pa: 'Pennsylvania',
  pr: 'Puerto Rico',
  ri: 'Rhode Island',
  sc: 'South Carolina',
  sd: 'South Dakota',
  tn: 'Tennessee',
  tx: 'Texas',
  ut: 'Utah',
  vt: 'Vermont',
  vi: 'Virgin Islands',
  va: 'Virginia',
  wa: 'Washington',
  wv: 'West Virginia',
  wi: 'Wisconsin',
  wy: 'Wyoming',
} as const;

const stateNameMappings: Record<UsaState, UsaStateCode> = {
  Alabama: 'al',
  Alaska: 'ak',
  'American Samoa': 'as',
  Arizona: 'az',
  Arkansas: 'ar',
  California: 'ca',
  Colorado: 'co',
  Connecticut: 'ct',
  Delaware: 'de',
  'District Of Columbia': 'dc',
  'Federated States Of Micronesia': 'fm',
  Florida: 'fl',
  Georgia: 'ga',
  Guam: 'gu',
  Hawaii: 'hi',
  Idaho: 'id',
  Illinois: 'il',
  Indiana: 'in',
  Iowa: 'ia',
  Kansas: 'ks',
  Kentucky: 'ky',
  Louisiana: 'la',
  Maine: 'me',
  'Marshall Islands': 'mh',
  Maryland: 'md',
  Massachusetts: 'ma',
  Michigan: 'mi',
  Minnesota: 'mn',
  Mississippi: 'ms',
  Missouri: 'mo',
  Montana: 'mt',
  Nebraska: 'ne',
  Nevada: 'nv',
  'New Hampshire': 'nh',
  'New Jersey': 'nj',
  'New Mexico': 'nm',
  'New York': 'ny',
  'North Carolina': 'nc',
  'North Dakota': 'nd',
  'Northern Mariana Islands': 'mp',
  Ohio: 'oh',
  Oklahoma: 'ok',
  Oregon: 'or',
  Palau: 'pw',
  Pennsylvania: 'pa',
  'Puerto Rico': 'pr',
  'Rhode Island': 'ri',
  'South Carolina': 'sc',
  'South Dakota': 'sd',
  Tennessee: 'tn',
  Texas: 'tx',
  Utah: 'ut',
  Vermont: 'vt',
  'Virgin Islands': 'vi',
  Virginia: 'va',
  Washington: 'wa',
  'West Virginia': 'wv',
  Wisconsin: 'wi',
  Wyoming: 'wy',
} as const;

export const ProvinceAndStateMappings: Record<ProvinceOrStateCode, ProvinceOrState> = Object.freeze({ ...provinceMappings, ...stateMappings });
export const ProvinceAndStateNameMappings: Record<ProvinceOrState, ProvinceOrStateCode> = Object.freeze({ ...provinceNameMappings, ...stateNameMappings });

export const provinceAndStateCodes = Object.keys(ProvinceAndStateMappings) as ProvinceOrStateCode[];
export const provinceAndStateSlugs = Object.values(ProvinceAndStateMappings).map(name => dasherize(name.toLowerCase()));

export const usStateCodes = Object.keys(stateMappings) as UsaStateCode[];
export const canadaProvinceCodes = Object.keys(provinceMappings) as CanadaProvinceCode[];

export function provinceOrStateNameFromCode(code: ProvinceOrStateCode) {
  return ProvinceAndStateMappings[code];
}

export function provinceOrStateCodeFromName(name: string): ProvinceOrStateCode | undefined {
  const normalizedName = name.toLowerCase();
  const matchingKey = Object.keys(ProvinceAndStateNameMappings).find(
    key => key.toLowerCase() === normalizedName
  );
  return matchingKey ? ProvinceAndStateNameMappings[matchingKey as ProvinceOrState] : undefined;
}

export function getProvinceOrStateCopy(provinceOrStateCode: string | null, city: string | null) {
  if (provinceOrStateCode) {
    if (city) {
      return provinceOrStateCode;
    } else {
      return provinceOrStateNameFromCode(provinceOrStateCode.toLowerCase() as ProvinceOrStateCode);
    }
  }
}

export function provinceOrStateCodeFromString(codeString: string): ProvinceOrStateCode | null {
  const sanitizedCodeString = codeString?.trim()?.toLowerCase();
  if (usStateCodes.includes(sanitizedCodeString as UsaStateCode)) {
    return sanitizedCodeString as UsaStateCode;
  } else if (canadaProvinceCodes.includes(sanitizedCodeString as CanadaProvinceCode)) {
    return sanitizedCodeString as CanadaProvinceCode;
  }
  return null;
}

export function provinceOrStateCodeFromSlug(slug: string): ProvinceOrStateCode | null {
  const sanitizedSlug = slug?.trim()?.toLowerCase();
  const swappedMappings = dasherizeKeys(swapKeysAndValues(ProvinceAndStateMappings));
  return swappedMappings?.[sanitizedSlug] as ProvinceOrStateCode || null;
}

export function provinceOrStateSlugFromCode(code: ProvinceOrStateCode) {
  const name = provinceOrStateNameFromCode(code);
  return name ? dasherize(name) : null;
}

/**
 * Builds the dasherized city-level slug for a city+province pair, matching
 * the pattern used for real area-page slugs (e.g. "toronto-on").
 */
export function citySlugFromCityProvince(city: string, province: string): string {
  return dasherize(`${city} ${province}`.toLowerCase());
}

export function isUsStateCode(code: ProvinceOrStateCode): boolean {
  return usStateCodes.includes(code as any);
}

export function isUsStateSlug(slug: string): boolean {
  const code = stateCodeFromSlugMappings[slug];
  return code ? usStateCodes.includes(code) : false;
}

export function isCanadianProvinceCode(code: ProvinceOrStateCode): boolean {
  return canadaProvinceCodes.includes(code as any);
}

export function isCanadianProvinceSlug(slug: string): boolean {
  const code = provinceCodeFromSlugMappings[slug];
  return code ? canadaProvinceCodes.includes(code) : false;
}

export function countryCodeFromProvinceOrState(name: string): CountryCode {
  if (name?.length > 0) {
    const isCanadianAdministrativeAreaLevel1Code = isCanadianProvinceCode(name.toLowerCase() as ProvinceOrStateCode);
    const isUsAdministrativeAreaLevel1Code = isUsStateCode(name.toLowerCase() as ProvinceOrStateCode);

    if (isCanadianAdministrativeAreaLevel1Code) {
      return CountryCodeList.CANADA;
    } else if (isUsAdministrativeAreaLevel1Code) {
      return CountryCodeList.UNITED_STATES;
    }
  }
  return CountryCodeList.CANADA;
}

/**
 * Maps province codes to their dasherized names
 * e.g., { on: 'ontario', pe: 'prince-edward-island', ... }
 */
export const provinceSlugMappings: Record<CanadaProvinceCode, string> = Object.entries(provinceMappings).reduce(
  (acc, [code, name]) => ({
    ...acc,
    [code]: dasherize(name.toLowerCase()),
  }),
  {} as Record<CanadaProvinceCode, string>
);

/**
 * Maps state codes to their dasherized names
 * e.g., { ny: 'new-york', ca: 'california', ... }
 */
export const stateSlugMappings: Record<UsaStateCode, string> = Object.entries(stateMappings).reduce(
  (acc, [code, name]) => ({
    ...acc,
    [code]: dasherize(name.toLowerCase()),
  }),
  {} as Record<UsaStateCode, string>
);

/**
 * Maps province slugs back to their province codes
 * e.g., { 'ontario': 'on', 'prince-edward-island': 'pe', ... }
 */
export const provinceCodeFromSlugMappings: Record<string, CanadaProvinceCode> = Object.entries(provinceSlugMappings).reduce(
  (acc, [code, slug]) => ({
    ...acc,
    [slug]: code as CanadaProvinceCode,
  }),
  {} as Record<string, CanadaProvinceCode>
);

/**
 * Maps state slugs back to their state codes
 * e.g., { 'new-york': 'ny', 'california': 'ca', ... }
 */
export const stateCodeFromSlugMappings: Record<string, UsaStateCode> = Object.entries(stateSlugMappings).reduce(
  (acc, [code, slug]) => ({
    ...acc,
    [slug]: code as UsaStateCode,
  }),
  {} as Record<string, UsaStateCode>
);

/**
 * Combined mapping of both province and state codes to their dasherized names
 */
export const provinceAndStateSlugMappings: Record<ProvinceOrStateCode, string> = {
  ...provinceSlugMappings,
  ...stateSlugMappings,
};

/**
 * Combined mapping of slugs back to their province or state codes
 */
export const provinceOrStateCodeFromSlugMappings: Record<string, ProvinceOrStateCode> = {
  ...provinceCodeFromSlugMappings,
  ...stateCodeFromSlugMappings,
};
